import { NIGHTMARES } from '@somnhold/content';
import { cellAtWorld, heroWalkable } from '../grid.ts';
import { dist2, idiv, isqrt, imax, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { Pt } from '../caza-types.ts';
import type { LagoonRules, LagoonState, LagoonTrack } from '../isla-types.ts';
import { enemiesNear, findEnemy, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { castSkill } from '../battle/skills.ts';
import { hasCell } from '../battle/isla-ice.ts';
import { neckLeft, neckStage } from '../battle/floes.ts';
import { lagoonHers, lagoonOrder } from '../battle/lagoon.ts';
import { segDist2 } from '../battle/isla-layout.ts';
import { slideEnd } from '../battle/seal.ts';
import { useSkills } from './ghost.ts';
import { rocaBot } from './roca-bot.ts';

/**
 * The lab's bots for the Long Hunt's chapter 4 (docs/sendas/barbaro.md, «La Isla Blanca»; docs/SENDAS.md,
 * «Calibración»), asked by ai/bote-bot.ts:
 *
 * - the floes (`floes`): who knows steps out of a crack as it creaks, never follows the pack onto a neck about to part,
 *   and goes for the colossi and the gnawers at the camp first; who doesn't fights what is nearest where it stands.
 * - the lagoon (`lagoon`): who knows goes from crossing to crossing of hers and, once one of hers is walked over by none,
 *   breaks the ice at its end; who doesn't breaks it at the end of the longest and clearest, then the next.
 * - Sigrun's last night (`seal`): who knows keeps the Seal between itself and nothing (never with the fire behind it),
 *   steps off its line, cuts the howls and waits at the crack it looks at; who doesn't stays by the fire and fights what
 *   comes. Both step off a marked line: anyone sees one.
 *
 * It only moves the hero, taps what a player would and picks its target; nothing it does is outside the rules.
 */

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const strike = (s: BattleState, h: Hero, t: Enemy): void => {
  useSkills(s, h, t);
  if (h.skillCd[2] === 0 && enemiesNear(s, t.x, t.y, 2500).length >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: t.x, y: t.y, id: t.id });
  h.target = t.id;
  h.forced = true;
};

/** A spot a step away off these cells (walkable, off them), or null. */
const stepOff = (s: BattleState, h: Hero, bad: (c: number) => boolean, far = 1300): Pt | null => {
  const dirs: [number, number][] = [
    [1000, 0],
    [500, 866],
    [-500, 866],
    [-1000, 0],
    [-500, -866],
    [500, -866],
  ];
  for (const k of [1, 2]) {
    let best: Pt | null = null;
    let bd = 0x7fffffff;
    for (const [dx, dy] of dirs) {
      const x = h.x + idiv(dx * far * k, 1000);
      const y = h.y + idiv(dy * far * k, 1000);
      const c = cellAtWorld(s, x, y);
      if (c < 0 || !heroWalkable(s, c) || bad(c)) continue;
      // Toward the middle of the board, by a little.
      const d = dist2(x, y, 0, 2000);
      if (d < bd) {
        bd = d;
        best = { x, y };
      }
    }
    if (best) return best;
  }
  return null;
};

/* ---------- the floes ---------- */

const floesBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.isla?.floes;
  const w = s.senda?.isla?.floes;
  if (!r || !w) return;
  const here = cellAtWorld(s, h.x, h.y);
  if (knows) {
    // Off a creaking crack, and off a neck about to part.
    const c = w.crack;
    const neckBad = (cell: number): boolean => r.necks.some((n, k) => hasCell(n, cell) && (neckStage(r, k, s.waveT) !== 0 || neckLeft(r, k, s.waveT) < 4 * 12));
    const bad = (cell: number): boolean => (!!c && hasCell(c.cells, cell)) || neckBad(cell);
    if (bad(here)) {
      const p = stepOff(s, h, bad);
      if (p) {
        h.target = 0;
        h.forced = false;
        return go(s, h, p.x, p.y, 200);
      }
    }
    // A floe whose neck is about to part: back to the shore.
    const onFloe = r.floeCells.findIndex((f) => hasCell(f, here));
    if (onFloe >= 0 && (neckStage(r, onFloe, s.waveT) !== 0 || neckLeft(r, onFloe, s.waveT) < 7 * 12)) {
      const p = r.landing[onFloe] as Pt;
      h.target = 0;
      h.forced = false;
      return go(s, h, p.x, p.y + 900, 300);
    }
    // At the camp first: the colossi, then the gnawers; else what's nearest, on the shore.
    const atCamp = nearestTo(enemiesNear(s, r.camp.x, r.camp.y, 5000, (e) => targetable(s, e) && (e.kind === 'frostColossus' || e.id % 3 === 0)), r.camp.x, r.camp.y);
    if (atCamp) return strike(s, h, atCamp);
    const col = nearestTo(enemiesNear(s, h.x, h.y, 16000, (e) => targetable(s, e) && e.kind === 'frostColossus' && hasCell(r.shore, cellAtWorld(s, e.x, e.y))), h.x, h.y);
    if (col) return strike(s, h, col);
    const t = nearestTo(enemiesNear(s, h.x, h.y, 16000, (e) => targetable(s, e) && !NIGHTMARES[e.kind].flying && (hasCell(r.shore, cellAtWorld(s, e.x, e.y)) || withinRange(h.x, h.y, e.x, e.y, 1600))), h.x, h.y);
    if (t) return strike(s, h, t);
    h.target = 0;
    return go(s, h, r.camp.x, r.camp.y - 2600, 600);
  }
  const t = nearestTo(enemiesNear(s, h.x, h.y, 16000, (e) => targetable(s, e)), h.x, h.y);
  if (t) return strike(s, h, t);
  h.target = 0;
};

/* ---------- the lagoon ---------- */

/** Her letters no seen crossing of hers shows walked over. */
const candidates = (r: LagoonRules, w: LagoonState): number[] => {
  const under = new Set<number>();
  r.crossings.forEach((c, i) => {
    if (w.seen[i] !== 1 || !lagoonHers(r, i)) return;
    const low = c.top === c.a ? c.b : c.a;
    under.add((r.tracks[low] as LagoonTrack).letter);
  });
  return [0, 1, 2, 3, 4, 5].filter((l) => !under.has(l) && w.ends[l] !== 2);
};

const trackOf = (r: LagoonRules, letter: number): LagoonTrack | undefined => r.tracks.find((t) => t.who === 0 && t.letter === letter);

const trackLength = (t: LagoonTrack | undefined): number => {
  if (!t) return 0;
  let n = 0;
  for (let i = 1; i < t.pts.length; i++) {
    const a = t.pts[i - 1] as Pt;
    const b = t.pts[i] as Pt;
    n += isqrt((b.x - a.x) * (b.x - a.x) + (b.y - a.y) * (b.y - a.y));
  }
  return n;
};

const lagoonBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.isla?.lagoon;
  const w = s.senda?.isla?.lagoon;
  if (!r || !w || w.done >= 0) return;
  // Wolves on it: fight them first.
  const near = nearestTo(enemiesNear(s, h.x, h.y, w.goal >= 0 ? 1500 : 2600, (e) => targetable(s, e)), h.x, h.y);
  if (near) {
    if (w.goal >= 0 && w.t0 >= 0) w.t0 = -1;
    return strike(s, h, near);
  }
  h.target = 0;
  h.forced = false;
  if (w.goal >= 0) {
    const p = r.ends[w.goal] as Pt;
    if (!withinRange(h.x, h.y, p.x, p.y, r.reach - 200)) go(s, h, p.x, p.y, 300);
    return;
  }
  const breakAt = (l: number): void => lagoonOrder(s, h, l);
  if (knows) {
    const left = candidates(r, w);
    if (left.length === 1) return breakAt(left[0] as number);
    // The nearest of her crossings not read yet.
    let best = -1;
    let bd = 0x7fffffff;
    r.crossings.forEach((c, i) => {
      if (w.seen[i] !== 0 || !lagoonHers(r, i)) return;
      const d = dist2(h.x, h.y, c.at.x, c.at.y);
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    const c = r.crossings[best];
    if (c) return go(s, h, c.at.x, c.at.y, 300);
    // All read and still more than one: the shortest of them (she walked less at the end).
    const pick = [...left].sort((a, b) => trackLength(trackOf(r, a)) - trackLength(trackOf(r, b)))[0];
    if (pick !== undefined) breakAt(pick);
    return;
  }
  // Who doesn't know: the longest and clearest first, then the next.
  const order = [0, 1, 2, 3, 4, 5].filter((l) => w.ends[l] === 0).sort((a, b) => trackLength(trackOf(r, b)) - trackLength(trackOf(r, a)));
  const pick = order[0] ?? [0, 1, 2, 3, 4, 5].find((l) => w.ends[l] !== 2);
  if (pick !== undefined) breakAt(pick);
};

/* ---------- Sigrun's last night ---------- */

const sealBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.isla?.seal;
  const w = s.senda?.isla?.seal;
  if (!r || !w || w.done >= 0) return;
  const e = findEnemy(s, w.id);
  // Off its marked line (anyone sees one).
  if (w.phase === 1 && segDist2(h.x, h.y, w.from, w.to) <= (r.slide.width + 500) * (r.slide.width + 500)) {
    const dx = w.to.x - w.from.x;
    const dy = w.to.y - w.from.y;
    const l = imax(1, isqrt(dx * dx + dy * dy));
    // Square to the line, to the side the hero is on.
    let nx = -idiv(dy * 2200, l);
    let ny = idiv(dx * 2200, l);
    if ((h.x - w.from.x) * nx + (h.y - w.from.y) * ny < 0) {
      nx = -nx;
      ny = -ny;
    }
    h.target = 0;
    h.forced = false;
    return go(s, h, h.x + nx, h.y + ny, 200);
  }
  if (!knows) {
    // By the fire, fighting what comes.
    const t = nearestTo(enemiesNear(s, r.fire.x, r.fire.y, 4500, (x) => targetable(s, x) && !(x.id === w.id && w.phase === 5)), h.x, h.y);
    if (t) return strike(s, h, t);
    h.target = 0;
    return go(s, h, r.fire.x, r.fire.y + 900, 500);
  }
  // Hurt and looking at a crack: there first, or on it.
  if (e && e.hp > 0 && (w.phase === 3 || w.phase === 4)) {
    const p = r.holes[w.hole] as Pt;
    if (!withinRange(h.x, h.y, p.x, p.y, r.block - 300)) {
      // At the crack's lip, on the ice side.
      const pl = imax(1, isqrt(p.x * p.x + p.y * p.y));
      h.target = 0;
      h.forced = false;
      return go(s, h, p.x - idiv(p.x * 650, pl), p.y - idiv(p.y * 650, pl), 150);
    }
    h.path = [];
    h.moving = false;
    if (withinRange(h.x, h.y, e.x, e.y, 1100)) return strike(s, h, e);
    return;
  }
  // Aiming at the fire: at it, so it turns.
  if (e && w.phase === 1 && w.atFire === 1) {
    h.target = 0;
    h.forced = false;
    return go(s, h, e.x, e.y, 300);
  }
  // A slide coming soon: near it (or it goes for the fire). Else a howl to cut, the gnawers at the fire.
  const soon = !!e && w.phase === 0 && w.slideNext - s.waveT < 5 * 12;
  const hw = w.howlers.find((x) => x.phase === 1 || x.phase === 2);
  const he = hw ? findEnemy(s, hw.id) : null;
  if (!soon && he && he.hp > 0 && withinRange(h.x, h.y, he.x, he.y, 6000)) return strike(s, h, he);
  const gn = nearestTo(enemiesNear(s, r.fire.x, r.fire.y, r.fireReach + 300, (x) => targetable(s, x) && x.id !== w.id), r.fire.x, r.fire.y);
  if (!soon && gn && (!e || !withinRange(e.x, e.y, r.fire.x, r.fire.y, 5000))) return strike(s, h, gn);
  if (e && e.hp > 0 && soon) {
    // A slide coming: to the Seal's side, square to the line from the fire (its line to the hero then misses the fire).
    const fx = e.x - r.fire.x;
    const fy = e.y - r.fire.y;
    const fl = imax(1, isqrt(fx * fx + fy * fy));
    let px = -idiv(fy * 1500, fl);
    let py = idiv(fx * 1500, fl);
    if ((h.x - e.x) * px + (h.y - e.y) * py < 0) {
      px = -px;
      py = -py;
    }
    const c = cellAtWorld(s, e.x + px, e.y + py);
    if (c < 0 || !heroWalkable(s, c)) {
      px = -px;
      py = -py;
    }
    if (!withinRange(h.x, h.y, e.x + px, e.y + py, 700)) {
      h.target = 0;
      h.forced = false;
      return go(s, h, e.x + px, e.y + py, 300);
    }
    return strike(s, h, e);
  }
  if (e && e.hp > 0 && w.phase !== 5) {
    // Never with the fire behind it: on the Seal's far side from the fire.
    const fx = e.x - r.fire.x;
    const fy = e.y - r.fire.y;
    const fl = imax(1, isqrt(fx * fx + fy * fy));
    const tx = e.x + idiv(fx * 1100, fl);
    const ty = e.y + idiv(fy * 1100, fl);
    if (!withinRange(h.x, h.y, tx, ty, 900) && segDist2(r.fire.x, r.fire.y, { x: e.x, y: e.y }, slideEnd(r, { x: e.x, y: e.y }, { x: h.x, y: h.y })) < (r.slide.width + 600) * (r.slide.width + 600)) {
      const c = cellAtWorld(s, tx, ty);
      if (c >= 0 && heroWalkable(s, c)) {
        h.target = 0;
        h.forced = false;
        return go(s, h, tx, ty, 300);
      }
    }
    return strike(s, h, e);
  }
  const t = nearestTo(enemiesNear(s, h.x, h.y, 16000, (x) => targetable(s, x) && x.id !== w.id), h.x, h.y);
  if (t) return strike(s, h, t);
  h.target = 0;
};

/** The chapter's act this bot plays (false: not one of its). */
export const islaBot = (s: BattleState, h: Hero, knows: boolean): boolean => {
  const r = s.rules?.senda?.isla;
  if (r?.floes) floesBot(s, h, knows);
  else if (r?.lagoon) lagoonBot(s, h, knows);
  else if (r?.seal) sealBot(s, h, knows);
  else return rocaBot(s, h, knows);
  return true;
};
